// Editing one workflow. The drawer follows the same three-part sentence the row
// shows, in the same order: when, then do this, stop when. No canvas, no
// branching — reordering steps is two arrows, which is all a linear sequence
// ever needs.

import React, { useEffect, useState } from "react";
import { X, Plus, Trash2, ArrowUp, ArrowDown, Check } from "lucide-react";
import Drawer from "../../../overlays/Drawer";
import { Button, Input, Select } from "../../../atoms";
import { TriggerChip, ActionChip, StepGap, uiOf } from "./chips";
import {
  TRIGGERS, LEAD_STATUSES, ACTIONS, STOPS, actionOf, makeStep, describeWait, totalDays,
} from "./workflowRules.mjs";

const WAITS = [0, 1, 2, 3, 4, 7, 14, 30];

function Section({ n, title, hint, children }) {
  return (
    <section style={{ padding: "14px 16px", borderBottom: "1px solid var(--color-border-subtle)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{
          width: 18, height: 18, borderRadius: 5, flexShrink: 0,
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          background: "var(--color-bg-muted)", color: "var(--color-text-secondary)",
          fontSize: 11, fontWeight: 600,
        }}>
          {n}
        </span>
        <h4 style={{ margin: 0, fontSize: 13, fontWeight: 600, color: "var(--color-text-primary)" }}>{title}</h4>
      </div>
      {hint && (
        <p style={{ margin: "4px 0 0 26px", fontSize: 11.5, color: "var(--color-text-muted)", lineHeight: "17px" }}>
          {hint}
        </p>
      )}
      <div style={{ marginTop: 10 }}>{children}</div>
    </section>
  );
}

function StepCard({ step, index, count, onChange, onMove, onRemove }) {
  const { color } = uiOf(step.action);

  return (
    <div style={{
      border: "1px solid var(--color-border-subtle)", borderRadius: 8, overflow: "hidden",
      background: "var(--color-bg-surface)",
    }}>
      <div style={{
        display: "flex", alignItems: "center", gap: 8, padding: "8px 10px",
        // A single solid rule in the action's colour, so a step card is
        // identifiable at a glance without tinting the whole card.
        borderLeft: `3px solid ${color}`,
        background: "var(--color-bg-base)",
      }}>
        <span style={{
          fontSize: 11, fontWeight: 600, color: "var(--color-text-muted)",
          width: 14, flexShrink: 0, fontVariantNumeric: "tabular-nums",
        }}>
          {index + 1}
        </span>

        <div style={{ width: 132, flexShrink: 0 }}>
          <Select value={step.action} onChange={(e) => onChange({ action: e.target.value })} size="small">
            {ACTIONS.map((a) => <option key={a.id} value={a.id}>{a.does}</option>)}
          </Select>
        </div>

        <div style={{ width: 118, flexShrink: 0 }}>
          <Select
            value={step.waitDays}
            onChange={(e) => onChange({ waitDays: Number(e.target.value) })}
            size="small"
            disabled={index === 0}
          >
            {/* Step one always goes out with the trigger, so there is nothing to pick. */}
            {index === 0
              ? <option value={0}>when it fires</option>
              : WAITS.map((d) => <option key={d} value={d}>{describeWait(d)}</option>)}
          </Select>
        </div>

        <span style={{ flex: 1 }} />

        <button
          type="button" onClick={() => onMove(-1)} disabled={index === 0}
          title="Move up" aria-label="Move step up"
          style={arrowBtn(index === 0)}
        >
          <ArrowUp size={12} />
        </button>
        <button
          type="button" onClick={() => onMove(1)} disabled={index === count - 1}
          title="Move down" aria-label="Move step down"
          style={arrowBtn(index === count - 1)}
        >
          <ArrowDown size={12} />
        </button>
        <button
          type="button" onClick={onRemove} title="Remove step" aria-label="Remove step"
          style={{ ...arrowBtn(false), color: "var(--crm-danger)" }}
        >
          <Trash2 size={12} />
        </button>
      </div>

      <textarea
        value={step.text}
        onChange={(e) => onChange({ text: e.target.value })}
        placeholder={`What should the ${actionOf(step.action).label.toLowerCase()} say? Write it as an instruction — "read their website, then name one thing I noticed".`}
        style={{
          width: "100%", border: "none", borderTop: "1px solid var(--color-border-subtle)",
          padding: "9px 11px", fontSize: 12.5, lineHeight: "18px", minHeight: 64,
          resize: "vertical", outline: "none", fontFamily: "inherit",
          color: "var(--color-text-primary)", background: "var(--color-bg-surface)", display: "block",
        }}
      />
    </div>
  );
}

const arrowBtn = (disabled) => ({
  display: "inline-flex", alignItems: "center", justifyContent: "center",
  width: 24, height: 24, borderRadius: 5, flexShrink: 0,
  border: "1px solid var(--color-border-subtle)", background: "var(--color-bg-surface)",
  color: "var(--color-text-secondary)", cursor: disabled ? "not-allowed" : "pointer",
  opacity: disabled ? 0.4 : 1,
});

export default function WorkflowEditor({ rule, onClose, onSave }) {
  const [draft, setDraft] = useState(rule);

  // Re-seed when a different rule opens. Keyed on id rather than the object so
  // typing in the drawer doesn't get reverted by the parent re-rendering.
  useEffect(() => { setDraft(rule); }, [rule?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!draft) return <Drawer open={false} onClose={onClose} width={560}><span /></Drawer>;

  const set = (next) => setDraft((d) => ({ ...d, ...next }));
  const setTrigger = (next) => setDraft((d) => ({ ...d, trigger: { ...d.trigger, ...next } }));
  const setStep = (id, next) =>
    setDraft((d) => ({ ...d, steps: d.steps.map((s) => (s.id === id ? { ...s, ...next } : s)) }));

  const moveStep = (index, delta) =>
    setDraft((d) => {
      const steps = [...d.steps];
      const to = index + delta;
      if (to < 0 || to >= steps.length) return d;
      [steps[index], steps[to]] = [steps[to], steps[index]];
      // The first step always fires with the trigger, so a wait that rode along
      // with a step into position 0 has to be dropped or the rule would claim to
      // wait before it has even started.
      if (steps[0]) steps[0] = { ...steps[0], waitDays: 0 };
      return { ...d, steps };
    });

  const toggleStop = (id) =>
    setDraft((d) => ({
      ...d,
      stops: d.stops.includes(id) ? d.stops.filter((s) => s !== id) : [...d.stops, id],
    }));

  const triggerDef = TRIGGERS.find((t) => t.id === draft.trigger.event);
  const days = totalDays(draft.steps);
  const problem =
    !draft.name.trim() ? "Give it a name."
      : draft.steps.length === 0 ? "Add at least one step."
        : draft.steps.some((s) => !s.text.trim()) ? "Every step needs an instruction."
          : draft.stops.length === 0 ? "Pick at least one thing that stops it."
            : null;

  return (
    <Drawer open={!!rule} onClose={onClose} width={560}>
      <header style={{
        flexShrink: 0, display: "flex", alignItems: "flex-start", gap: 12,
        padding: "14px 16px", borderBottom: "1px solid var(--color-border-subtle)",
      }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 11, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.07em", fontWeight: 500 }}>
            Workflow
          </div>
          <Input
            value={draft.name}
            onChange={(e) => set({ name: e.target.value })}
            placeholder="Name it after what it does"
            style={{ marginTop: 5, fontSize: 15, fontWeight: 600, height: 34 }}
          />
          {/* the same sentence the row will show, updating as you edit */}
          <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 9, flexWrap: "wrap" }}>
            <TriggerChip trigger={draft.trigger} />
            <span aria-hidden="true" style={{ color: "var(--color-text-muted)", fontSize: 11 }}>then</span>
            {draft.steps.map((s, i) => (
              <React.Fragment key={s.id}>
                {i > 0 && <StepGap waitDays={s.waitDays} />}
                <ActionChip action={s.action} />
              </React.Fragment>
            ))}
            {days > 0 && (
              <span style={{ fontSize: 11, color: "var(--color-text-muted)" }}>over {days} days</span>
            )}
          </div>
        </div>
        <button
          type="button" onClick={onClose} aria-label="Close"
          style={{ background: "transparent", border: "none", cursor: "pointer", color: "var(--color-text-muted)", padding: 2, flexShrink: 0 }}
        >
          <X size={16} />
        </button>
      </header>

      <div style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
        <Section n={1} title="When this happens" hint={triggerDef?.hint}>
          <div style={{ display: "flex", gap: 8 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <Select value={draft.trigger.event} onChange={(e) => setTrigger({ event: e.target.value })}>
                {TRIGGERS.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
              </Select>
            </div>
            {triggerDef?.takes === "status" && (
              <div style={{ width: 130, flexShrink: 0 }}>
                <Select value={draft.trigger.status || "hot"} onChange={(e) => setTrigger({ status: e.target.value })}>
                  {LEAD_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </Select>
              </div>
            )}
            {triggerDef?.takes === "days" && (
              <div style={{ width: 130, flexShrink: 0 }}>
                <Select value={draft.trigger.days ?? 7} onChange={(e) => setTrigger({ days: Number(e.target.value) })}>
                  {[1, 2, 3, 5, 7, 14, 30].map((d) => <option key={d} value={d}>{d} days</option>)}
                </Select>
              </div>
            )}
            {triggerDef?.takes === "amount" && (
              <div style={{ width: 150, flexShrink: 0 }}>
                <Input
                  type="number" min={0} value={draft.trigger.amount ?? 0}
                  onChange={(e) => setTrigger({ amount: Number(e.target.value) })}
                />
              </div>
            )}
          </div>
        </Section>

        <Section
          n={2}
          title="Then do this, in order"
          hint="Each step is written by AI for that specific lead, using your instruction."
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {draft.steps.map((s, i) => (
              <StepCard
                key={s.id}
                step={s}
                index={i}
                count={draft.steps.length}
                onChange={(next) => setStep(s.id, next)}
                onMove={(delta) => moveStep(i, delta)}
                onRemove={() => set({ steps: draft.steps.filter((x) => x.id !== s.id) })}
              />
            ))}
            <Button
              size="sm" variant="secondary" leftIcon={<Plus size={13} />}
              onClick={() => set({ steps: [...draft.steps, makeStep(draft.steps.length ? "email" : "whatsapp")] })}
              style={{ alignSelf: "flex-start" }}
            >
              Add step
            </Button>
          </div>
        </Section>

        <Section n={3} title="Stop when" hint="Pick anything that means we should stop messaging.">
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {STOPS.map((s) => {
              const on = draft.stops.includes(s.id);
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => toggleStop(s.id)}
                  aria-pressed={on}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 6,
                    height: 28, padding: "0 10px", borderRadius: 6, cursor: "pointer",
                    fontSize: 12, fontWeight: on ? 500 : 400,
                    border: `1px solid ${on ? "var(--crm-primary)" : "var(--color-border-subtle)"}`,
                    background: on ? "var(--crm-primary)" : "var(--color-bg-surface)",
                    color: on ? "var(--crm-primary-text)" : "var(--color-text-secondary)",
                    transition: "background-color 120ms ease",
                  }}
                >
                  {on && <Check size={12} strokeWidth={2.6} />}
                  {s.label}
                </button>
              );
            })}
          </div>
        </Section>
      </div>

      <footer style={{
        flexShrink: 0, display: "flex", alignItems: "center", gap: 10,
        padding: "12px 16px", borderTop: "1px solid var(--color-border-subtle)",
      }}>
        <span style={{ flex: 1, fontSize: 11.5, color: problem ? "var(--crm-danger)" : "var(--color-text-muted)" }}>
          {problem || (draft.enabled ? "This one is on." : "Saved off — turn it on from the list.")}
        </span>
        <Button size="sm" variant="secondary" onClick={onClose}>Cancel</Button>
        <Button size="sm" onClick={() => onSave(draft)} disabled={!!problem}>Save</Button>
      </footer>
    </Drawer>
  );
}
